import React, { useMemo } from 'react'
import { ClearingHouse, DealSettlement, EntityType, NegotiationDetail, SettlementVenue, Sponsor, SpreadsheetCsd } from 'api'
import { Field } from 'view/components/inputs'
import { ComboboxProps, Selectbox, SelectboxProps } from 'view/components/options'
import { CsdHelper, getEnumValues, SettlementVenueHelper } from 'model'
import { createEntityCombobox, createEntitySelectbox } from './EntityOptions'
import { createStructuredSelector } from 'reselect'
import {
  createClearingSponsorIdsSelector,
  createCounterpartyClearingSponsorIdsSelector,
  createHasDirectClearingRelationshipSelector
} from 'selectors/clearingSelectors'
import { connect } from 'react-redux'
import { createSettlementVenuesIncomingUSSettlementSelector } from 'selectors/settlementSelectors'

/* CSD */

export type CsdSelectboxProps = Omit<SelectboxProps<DealSettlement, DealSettlement>, 'optionKeys'>

export const CsdSelectbox = React.forwardRef((props: CsdSelectboxProps, ref: React.Ref<Selectbox>) => {
  const settlementOptions = useMemo(() => Object.values(SpreadsheetCsd), [])

  return <Selectbox ref={ref} optionKeys={settlementOptions} getOptionText={CsdHelper.getName} {...props} />
})

export const CsdSelectboxField = Field(CsdSelectbox)

/* All Settlement Venues */

export const availableSettlementVenues: string[] = getEnumValues(SettlementVenue).filter(
  settlementVenue => settlementVenue !== SettlementVenue.Triparty
)

const mapAvailableSettlementVenuesToProps = createStructuredSelector({
  optionKeys: () => availableSettlementVenues
})

export type SettlementVenueSelectboxProps = Omit<SelectboxProps<string, string>, 'getOptionText'>
export const SettlementVenueSelectboxComponent: React.FunctionComponent<SettlementVenueSelectboxProps> = props => {
  return <Selectbox getOptionText={SettlementVenueHelper.getName} {...props} />
}

export const SettlementVenueIncomingSettlementSelectbox = connect(
  createStructuredSelector({
    optionKeys: createSettlementVenuesIncomingUSSettlementSelector(
      (_: {}, props: { negotiationDetail: NegotiationDetail }) => props.negotiationDetail
    )
  }),
  null,
  null,
  { forwardRef: true }
)((SettlementVenueSelectboxComponent as any) as React.ComponentType<SettlementVenueSelectboxProps>)

export const AllSettlementVenueSelectbox = connect(mapAvailableSettlementVenuesToProps)(SettlementVenueSelectboxComponent)
export const AllSettlementVenueSelectboxField = Field(AllSettlementVenueSelectbox)

/* Restricted Settlement Venues */
export const SettlementVenueSelectboxField = Field(SettlementVenueSelectboxComponent)

/* Clearing House */

const ClearingHouseEntitySelectbox = createEntitySelectbox(EntityType.CLEARING_HOUSE)
const ClearingHouseSelectbox: React.FunctionComponent<SelectboxProps<ClearingHouse, string>> = props => {
  return <ClearingHouseEntitySelectbox {...props} />
}

export const ClearingHouseSelectboxField = Field(ClearingHouseSelectbox)

/* Sponsor */

const SponsorEntitySelectbox = createEntitySelectbox(EntityType.SPONSOR)
const SponsorSelectbox: React.FunctionComponent<SelectboxProps<Sponsor, string>> = props => {
  return <SponsorEntitySelectbox {...props} />
}

export const SponsorSelectboxField = Field(SponsorSelectbox)

const SponsorCombobox = createEntityCombobox(EntityType.SPONSOR)

export interface CounterpartySponsorComboboxProps extends ComboboxProps<Sponsor, string> {
  counterpartyId: string
  clearingHouseId: string | null | undefined
  productSectorId: string | null | undefined
  hasDirectClearingRelationship: boolean
}

const counterpartySponsorCounterpartyIdSelector = (_: {}, props: CounterpartySponsorComboboxProps) => props.counterpartyId
const counterpartySponsorClearingHouseIdSelector = (_: {}, props: CounterpartySponsorComboboxProps) => props.clearingHouseId
const counterpartySponsorProductSectorIdSelector = (_: {}, props: CounterpartySponsorComboboxProps) => props.productSectorId

const counterpartySponsorMapStateToProps = createStructuredSelector({
  optionKeys: createCounterpartyClearingSponsorIdsSelector(
    counterpartySponsorCounterpartyIdSelector,
    counterpartySponsorClearingHouseIdSelector,
    counterpartySponsorProductSectorIdSelector
  ),
  hasDirectClearingRelationship: createHasDirectClearingRelationshipSelector(
    counterpartySponsorCounterpartyIdSelector,
    counterpartySponsorClearingHouseIdSelector,
    counterpartySponsorProductSectorIdSelector
  )
})

const CounterpartySponsorComboboxComponent: React.FunctionComponent<CounterpartySponsorComboboxProps> = props => {
  const { counterpartyId, clearingHouseId, hasDirectClearingRelationship, ...rest } = props
  return <SponsorCombobox canClear={hasDirectClearingRelationship} {...rest} />
}

export const CounterpartySponsorCombobox = connect(counterpartySponsorMapStateToProps)(
  CounterpartySponsorComboboxComponent
) as React.ComponentType<Omit<CounterpartySponsorComboboxProps, 'optionKeys' | 'hasDirectClearingRelationship'>>

export const CounterpartySponsorComboboxField = Field(CounterpartySponsorCombobox)

export interface DealSponsorComboboxProps extends ComboboxProps<Sponsor, string> {
  fundId: string
  clearingHouseId: string | null
}

const dealSponsorFundIdSelector = (_: {}, props: DealSponsorComboboxProps) => props.fundId
const dealSponsorClearingHouseIdSelector = (_: {}, props: DealSponsorComboboxProps) => props.clearingHouseId

const dealSponsorMapStateToProps = createStructuredSelector({
  optionKeys: createClearingSponsorIdsSelector(dealSponsorFundIdSelector, dealSponsorClearingHouseIdSelector)
})

export const DealSponsorComboboxComponent: React.FunctionComponent<DealSponsorComboboxProps> = props => {
  const { fundId, clearingHouseId, ...rest } = props
  return <SponsorCombobox {...rest} />
}

export const DealSponsorCombobox = connect(dealSponsorMapStateToProps)(DealSponsorComboboxComponent) as React.ComponentType<
  Omit<DealSponsorComboboxProps, 'optionKeys'>
>

export const DealSponsorComboboxField = Field(DealSponsorCombobox)
